import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { fetchMe, login as apiLogin, logout as apiLogout } from '../lib/api'

const AuthContext = createContext(null)

// Mirrors config/systeza.php 'plans.*.modules' on the API. Update both together.
const PLAN_MODULES = {
  free:       [],
  starter:    ['infra'],
  growth:     ['infra', 'engage'],
  pro:        ['infra', 'engage', 'automations', 'hub'],
  enterprise: ['infra', 'engage', 'automations', 'hub'],
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null)
  const [loading, setLoading] = useState(true)

  const loadUser = useCallback(async () => {
    try {
      const userData = await fetchMe()
      setUser(userData)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadUser() }, [loadUser])

  const login = async (email, password, remember) => {
    const data = await apiLogin(email, password, remember)
    if (data.two_factor_required) return data
    setUser(data.user)
    return data
  }

  const logout = async () => {
    await apiLogout()
    setUser(null)
  }

  const refreshUser = async () => {
    const userData = await fetchMe()
    setUser(userData)
  }

  const hasPermission = (permission) => {
    if (!user) return false
    if (user.role === 'super_admin') return true
    if (user.permissions?.includes('*')) return true
    return user.permissions?.includes(permission) ?? false
  }

  // UI gating only — never an authorisation boundary. The server enforces the
  // same map via the plan.module middleware (config/systeza.php 'plans').
  // Keep PLAN_MODULES in sync with that config.
  const hasModule = (module) => {
    if (!user) return false
    if (user.role === 'super_admin' || user.role === 'admin') return true
    const plan = user.plan || 'free'
    return (PLAN_MODULES[plan] || []).includes(module)
  }

  const isAdmin      = ['admin','super_admin'].includes(user?.role)
  const isSuperAdmin = user?.role === 'super_admin'

  // Platform admin is gated by RANK, not by role equality — see
  // App\Support\PlatformRoles on the API. admin(2) < super_admin(3) <
  // platform_admin(4), and a gate admits everyone at or above its lowest role.
  // UI gating only; the platform.role middleware is the real boundary.
  const PLATFORM_RANKS = { admin: 2, super_admin: 3, platform_admin: 4 }
  const platformRank   = PLATFORM_RANKS[user?.role] ?? 0
  const isPlatformAdmin      = platformRank >= 2   // the admin surface
  const isPlatformSuperAdmin = platformRank >= 3   // settings, which hold live secrets
  const isClient     = user?.role === 'client'
  const isUser       = user?.role === 'user' // engage/automations user

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, refreshUser,
      hasPermission, hasModule,
      isAdmin, isSuperAdmin, isClient, isUser,
      isPlatformAdmin, isPlatformSuperAdmin, platformRank,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
